import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogoMark } from '../components/LogoMark';
import { env } from '../config/env';

export function LoginPage() {
  const { status, signInWithGoogle, signInWithMicrosoft, error } = useAuth();
  const [ssoSubmitting, setSsoSubmitting] = useState<'google' | 'microsoft' | null>(null);

  if (
    status === 'authorized' ||
    status === 'two-factor-required' ||
    status === 'two-factor-enrollment-required'
  ) {
    return <Navigate to="/" replace />;
  }

  const handleGoogleSignIn = async () => {
    setSsoSubmitting('google');
    try {
      await signInWithGoogle();
    } catch {
      // error surfaced via useAuth().error
    } finally {
      setSsoSubmitting(null);
    }
  };

  const handleMicrosoftSignIn = async () => {
    setSsoSubmitting('microsoft');
    try {
      await signInWithMicrosoft();
    } catch {
      // error surfaced via useAuth().error
    } finally {
      setSsoSubmitting(null);
    }
  };

  return (
    <div className="login-screen">
      {/* Same split-screen shape as the Simply White source's login.html (full-bleed
          background layer + fixed-width white form panel) — filled with MediclynkOS's own
          dark-mode canvas tokens and a cyan glow instead of the source's stock photo. */}
      <div className="login-hero">
        <div className="login-hero-mark" aria-hidden="true">
          <LogoMark size={480} />
        </div>
        <div className="login-hero-content">
          <LogoMark size={40} />
          <h1>The command console for MedicLynk&rsquo;s care network.</h1>
          <p>
            Specialist verification, live case oversight, trust &amp; safety, and payouts —
            unified in one place.
          </p>
        </div>
        <div className="login-hero-foot">© {new Date().getFullYear()} MedicLynk. Internal use only.</div>
      </div>

      <div className="login-panel">
        <div className="login-panel-inner">
          <LogoMark size={36} />
          <h2>Get started with your dashboard</h2>
          <p className="auth-subtitle">Sign in to your account</p>

          {/* Email/password login was removed — the admin portal is SSO-only now. Access is
              gated server-side (malavecorporation.com / mediclynk.com / mediclynkos.com email
              domain allowlist enforced on /auth/google/exchange and /auth/microsoft/exchange),
              so an account outside the allowlist still surfaces a clear rejection below. */}
          {error && <p className="auth-error" role="alert">{error}</p>}

          <button
            type="button"
            className="auth-sso-button"
            onClick={handleGoogleSignIn}
            disabled={ssoSubmitting !== null}
          >
            <GoogleGlyph />
            {ssoSubmitting === 'google' ? 'Signing in…' : 'Sign in with Google'}
          </button>

          {env.microsoftClientId && (
            <button
              type="button"
              className="auth-sso-button"
              onClick={handleMicrosoftSignIn}
              disabled={ssoSubmitting !== null}
            >
              <MicrosoftGlyph />
              {ssoSubmitting === 'microsoft' ? 'Signing in…' : 'Sign in with Microsoft'}
            </button>
          )}

          <p className="login-legal">
            Access is restricted to MedicLynk staff. Contact an administrator if you believe you
            should have access.
          </p>
        </div>
      </div>
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.87 2.69-6.62Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.96 10.71a5.4 5.4 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l3-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58Z" />
    </svg>
  );
}

function MicrosoftGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <rect x="1" y="1" width="7.5" height="7.5" fill="#F25022" />
      <rect x="9.5" y="1" width="7.5" height="7.5" fill="#7FBA00" />
      <rect x="1" y="9.5" width="7.5" height="7.5" fill="#00A4EF" />
      <rect x="9.5" y="9.5" width="7.5" height="7.5" fill="#FFB900" />
    </svg>
  );
}
